import { getDefaultImageLayerData } from '@meitu/saas-editor-core/es/layers/image-layer/constants/default-image-data';
import { getImgLayerProps } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import { uploadBase64ToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { CanvasType, ILayerProps, PosterType } from '@meitu/saas-editor-core/es/version-types';
import { DownloadImgType } from '@meitu/saas-editor-ui/es/utils/stage';
import useOpenPlugins from '@/editor/collapse/views/more-collapse/hooks/use-open-plugins';
import { editor } from '@/instance';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { useRecordModel } from '@/store/models/record-model';
import { useTemplateModel } from '@/store/models/template-model';
import { initMenu } from './init-menu';

/**
 * 初始化设计师编辑器的空白模板，插入画布后生成预览图
 *
 * 在模板加载流程中挂载，依赖编辑器实例及模板、菜单状态
 *
 * @returns 空白模板初始化函数
 */
export const useInitEmptyTemplate = () => {
    const setPreview = useTemplateModel((state) => state.setPreview);
    const setPosterType = useRecordModel((state) => state.setPosterType);
    const { openPlugin } = useOpenPlugins();
    const setCurrentType = useCollapseModel((state) => state.setCurrentType);

    return async function (
        width: number,
        height: number,
        unit: SizeUnitType,
        imgUrl?: string,
        // createInfo 参考 https://cf.meitu.com/confluence/pages/viewpage.action?pageId=328480736
        createInfo?: Record<string, any>,
        stageInfo?: { posterType: PosterType; canvasType: CanvasType },
    ) {
        editor.services.editor.setUnit(unit);
        setPosterType(stageInfo?.posterType ?? PosterType.EmptyPoster);

        const finalLayers: ILayerProps[] = [];

        const backgroundColor = createInfo?.bg?.backgroundColor || '#ffffffff';
        const bgLayer = editor.backgroundLayer.create({
            width,
            height,
            backgroundColor,
        });

        finalLayers.push(bgLayer);

        if (imgUrl) {
            const imageLayerData = getDefaultImageLayerData(
                imgUrl,
                createInfo?.mainImage?.width || width,
                createInfo?.mainImage?.height || height,
                createInfo?.mainImage?.left || 0,
                createInfo?.mainImage?.top || 0,
                '',
                createInfo?.mainImage?.naturalWidth || width,
                createInfo?.mainImage?.naturalHeight || height,
                false,
            );
            // 获取图片图层数据
            const layer = getImgLayerProps(imageLayerData);
            finalLayers.push(layer);
        } else if (stageInfo?.posterType === PosterType.NormalPPT) {
            // 正常PPT
        } else {
            // const text = isInitAiText() ? { text: $t((v) => v.hooks.editOnLeft) } : {};
            // const color = checkIsDarkColor(backgroundColor) ? '#ffffffff' : '#000000ff';
            // const textLayer = await createTextLayer(stageSize, { ...text, color });
            // finalLayers.push(textLayer);
        }

        // 插入图层
        await editor.services.stage.insertWithoutHistory([finalLayers], [{ width, height, threshold: 0 }]);
        if (imgUrl && finalLayers[1]) {
            editor.services.frame.setActiveLayer(finalLayers[1].id);
        }
        const { cover } = await editor.download.drawStages(1, DownloadImgType.png);
        const previewCanvas = await editor.download.scaleContain(cover, 400, 400 / (width / height));
        const { url } = await uploadBase64ToObs(previewCanvas.toDataURL('image/png'));
        setPreview(url);

        setCurrentType(initMenu(stageInfo?.canvasType ?? CanvasType.NORMAL, undefined, openPlugin));
    };
};
